<script lang="ts">
  import { onMount } from "svelte";
  import L from "leaflet";
  import "leaflet/dist/leaflet.css";
  import type { DealerMapCanvasProps } from "../../types/dealer-map-canvas-props";

  let { dealers }: DealerMapCanvasProps = $props();
  let mapElement: HTMLDivElement;
  let map: L.Map | null = null;
  let markerLayer: L.LayerGroup | null = null;

  const getThemeColor = (variableName: string) => {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(variableName)
      .trim();
  };

  const getSupplierColor = (supplier: string | null) => {
    if (supplier === "Attityde") return getThemeColor("--chart-1");
    if (supplier === "CarAds") return getThemeColor("--chart-2");
    if (supplier === "Auto IT") return getThemeColor("--chart-4");
    if (supplier?.startsWith("Other:")) return getThemeColor("--chart-5");
    if (supplier === "No website") return getThemeColor("--muted-foreground");
    return getThemeColor("--chart-3");
  };

  const addTextRow = (container: HTMLElement, label: string, value: string) => {
    const row = document.createElement("p");
    const strong = document.createElement("strong");
    strong.textContent = `${label}: `;
    row.append(strong, document.createTextNode(value));
    container.append(row);
  };

  const buildPopup = (dealer: DealerMapCanvasProps["dealers"][number]) => {
    const container = document.createElement("div");
    container.className = "dealer-map-popup";

    const title = document.createElement("h3");
    title.textContent = dealer.name;
    container.append(title);

    if (dealer.address) addTextRow(container, "Adresse", dealer.address);
    addTextRow(container, "Leverandør", dealer.websiteSupplier ?? "Unknown");
    addTextRow(
      container,
      "Brugtbilmodul",
      dealer.hasUsedCarModule ? "Ja" : "Nej"
    );
    if (dealer.resolvedIpAddresses.length > 0) {
      addTextRow(container, "IP", dealer.resolvedIpAddresses.join(", "));
    }

    const links = document.createElement("div");
    links.className = "dealer-map-popup-links";

    if (dealer.websiteUrl) {
      const websiteLink = document.createElement("a");
      websiteLink.href = dealer.websiteUrl;
      websiteLink.target = "_blank";
      websiteLink.rel = "noopener noreferrer";
      websiteLink.textContent = "Hjemmeside";
      links.append(websiteLink);
    }

    if (dealer.usedCarModuleUrl) {
      const moduleLink = document.createElement("a");
      moduleLink.href = dealer.usedCarModuleUrl;
      moduleLink.target = "_blank";
      moduleLink.rel = "noopener noreferrer";
      moduleLink.textContent = "Modulside";
      links.append(moduleLink);
    }

    const bilbasenLink = document.createElement("a");
    bilbasenLink.href = dealer.bilbasenUrl;
    bilbasenLink.target = "_blank";
    bilbasenLink.rel = "noopener noreferrer";
    bilbasenLink.textContent = "Bilbasen";
    links.append(bilbasenLink);
    container.append(links);

    if (dealer.supplierEvidence) {
      const evidence = document.createElement("small");
      evidence.textContent = dealer.supplierEvidence;
      container.append(evidence);
    }

    return container;
  };

  const renderMarkers = () => {
    if (!map || !markerLayer) return;

    markerLayer.clearLayers();
    const bounds: L.LatLngExpression[] = [];

    for (const dealer of dealers) {
      if (dealer.latitude === null || dealer.longitude === null) continue;

      const point: L.LatLngExpression = [dealer.latitude, dealer.longitude];
      bounds.push(point);
      L.circleMarker(point, {
        radius: 7,
        color: getThemeColor("--card"),
        weight: 2,
        fillColor: getSupplierColor(dealer.websiteSupplier),
        fillOpacity: 0.9
      })
        .bindTooltip(dealer.name)
        .bindPopup(buildPopup(dealer), { maxWidth: 340 })
        .addTo(markerLayer);
    }

    if (bounds.length === 1) {
      map.setView(bounds[0]!, 12);
    } else if (bounds.length > 1) {
      map.fitBounds(L.latLngBounds(bounds), {
        padding: [24, 24],
        maxZoom: 11
      });
    }
  };

  onMount(() => {
    map = L.map(mapElement, {
      center: [56.15, 10.1],
      zoom: 7,
      minZoom: 6
    });

    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(map);

    markerLayer = L.layerGroup().addTo(map);
    renderMarkers();

    return () => {
      map?.remove();
      map = null;
      markerLayer = null;
    };
  });

  $effect(() => {
    dealers;
    renderMarkers();
  });
</script>

<div class="dealer-map-canvas" bind:this={mapElement}></div>

<style>
  .dealer-map-canvas {
    height: min(72vh, 760px);
    min-height: 520px;
    width: 100%;
    overflow: hidden;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--muted);
  }

  :global(.dealer-map-popup) {
    display: grid;
    gap: 0.35rem;
    color: var(--foreground);
  }

  :global(.dealer-map-popup h3),
  :global(.dealer-map-popup p) {
    margin: 0;
  }

  :global(.dealer-map-popup-links) {
    display: flex;
    gap: 0.75rem;
    margin-top: 0.25rem;
  }

  :global(.dealer-map-popup a) {
    color: var(--accent);
    font-weight: 700;
  }

  :global(.dealer-map-popup small) {
    color: var(--muted-foreground);
  }

  :global(.leaflet-popup-content-wrapper),
  :global(.leaflet-popup-tip),
  :global(.leaflet-control-zoom a) {
    background: var(--card);
    color: var(--foreground);
  }

  :global(.leaflet-control-zoom a),
  :global(.leaflet-bar) {
    border-color: var(--border);
  }
</style>
